/*
 * Form Element Mixin
 * This mixin groups some common properties and methods for all form elements.
 */
import Vue from 'vue';
import isEmpty from 'lodash/isEmpty';
import isObject from 'lodash/isObject';
import ShouldApplyDefault from '@/modules/form/mixins/should-apply-default';
import { compareWithOperator } from '@/utils';
import { isString, isUndefined } from 'lodash';


export default {
  inject: ['$validator', '$parentForm'],
  mixins: [ShouldApplyDefault],
  provide() {
    return {
      $formField: this.name,
    };
  },
  props: {
    value: {
      type: String,
    },
    name: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      required: false,
    },
    validation: {
      type: [String, Object],
      required: false,
      default: null,
    },
    recommended: {
      type: Boolean,
    },
    requiredForPublish: {
      type: Boolean,
    },
    requiredForPublishMessage: {
      type: String,
    },
    requiredValueForPublish: {
      default: null,
    },
    disabled: {
      type: Boolean,
    },
    default: {
      type: undefined,
      default: undefined,
    },
  },
  computed: {
    scope() {
      return this.$parentForm.scope;
    },
    required() {
      if (this.validation === null) return false;
      if (!isObject(this.validation)) {
        return this.validation.includes('required');
      }
      return !!this.validation.required;
    },
    validForPublish() {
      if (this.requiredForPublish && this.empty) {
        return false;
      }
      if (this.requiredValueForPublish) {
        if (!isObject(this.requiredValueForPublish)
          && this.value !== this.requiredValueForPublish
        ) {
          return false;
        }

        if (!isString(this.requiredValueForPublish.objectKey)) {
          // eslint-disable-next-line max-len
          console.warn(`Object key is not a string, check validForPublish configuration for field ${this.name}`, this.requiredValueForPublish);
          return this.flags.valid;
        }

        if (isObject(this.value)) {
          const objectKey = this.requiredValueForPublish.objectKey;
          if (isUndefined(this.value[objectKey])) {
            // eslint-disable-next-line max-len
            console.warn(`Object key is undefined on object, check validForPublish configuration for field ${this.name}`);
            return this.flags.valid;
          }
          // eslint-disable-next-line max-len
          return compareWithOperator(this.value[objectKey], this.requiredValueForPublish.value, this.requiredValueForPublish.operator);
        }
      }
      return this.flags.valid;
    },
    empty() {
      if (this.value === ''
        || this.value === undefined
        || this.value === null) {
        return true;
      }
      // Check for empty objects/arrays
      if (typeof this.value === 'object' && isEmpty(this.value)) {
        return true;
      }
      return false;
    },
    errorMessages() {
      return this.$parentForm.errors.collect(this.name, this.scope);
    },
    flags() {
      if (this.scope
        && this.$validator.flags[`$${this.scope}`]) {
        return this.$validator.flags[`$${this.scope}`][this.name] || {};
      }
      return this.$validator.flags[this.name] || {};
    },
  },
  methods: {
    updateFieldFlags() {
      this.flags.empty = this.empty;
      this.flags.required = this.required;
      this.flags.requiredForPublish = this.requiredForPublish;
      this.flags.recommendedNotEmpty = this.recommended;
      this.flags.validForPublish = this.validForPublish;
    },
    onBlur($event) {
      this.$parentForm.updateField(this.name, $event.target.value);
      this.$emit('blur', $event);
      this.flags.focused = false;
    },
    onInput($event) {
      this.$emit('input', $event);
    },
    onFocus() {
      this.flags.focused = true;
    },
    // Performs a vee-validate validation update
    async validate() {
      let result = false;
      try {
        const field = (this.scope ? `${this.scope}.${this.name}` : this.name);
        result = await this.$validator.validate(field, this.value);
        return result;
      } catch (error) {
        console.warn(error);
        return false;
      }
    },
    /**
     * Default value handler.
     * Implemented as a method so it can be overrided.
     */
    onDefault(defaultValue) {
      this.$emit('input', defaultValue);
      this.$parentForm.updateField(this.name, defaultValue);
      this.$emit('blur', defaultValue);
    },
  },
  watch: {
    empty() {
      this.flags.empty = this.empty;
    },
    validForPublish() {
      this.flags.validForPublish = this.validForPublish;
    },
    default(value) {
      if (this.shouldApplyDefault(this.value, value)) {
        this.onDefault(this.default);
      }
    },
  },
  mounted() {
    if (this.shouldApplyDefault(this.value, this.default)) {
      this.onDefault(this.default);
    }

    Vue.util.defineReactive(this.flags, 'empty', this.empty);
    Vue.util.defineReactive(this.flags, 'recommendedNotEmpty', this.recommended);
    Vue.util.defineReactive(this.flags, 'recommended', false);
    Vue.util.defineReactive(this.flags, 'required', this.required);
    Vue.util.defineReactive(this.flags, 'requiredForPublish', this.requiredForPublish);
    Vue.util.defineReactive(this.flags, 'requiredValueForPublish', this.requiredValueForPublish);
    Vue.util.defineReactive(this.flags, 'validForPublish', this.validForPublish);
    Vue.util.defineReactive(this.flags, 'focused', false);
  },
};
